import React, { useState, useEffect } from 'react';
import { Shield, Users, ChevronRight, Eye, Building2, UserCog, Briefcase, User } from 'lucide-react';
import { roleService } from '../../../services/roleService';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';


/**
 * RoleHierarchyVisual Component
 * 
 * Workflow-style visualization showing role hierarchy and access levels
 * Supports Super Admin role switching to view Business Administrator scopes
 */
export default function RoleHierarchyVisual() {
  const { isSuperAdmin } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    loadRolesAndTenants();
  }, [selectedTenant]);

  const loadRolesAndTenants = async () => {
    try {
      setLoading(true);
      const rolesData = await roleService?.getAllRoles();
      setRoles(rolesData);

      // Load tenants for Super Admin role switching
      if (isSuperAdmin()) {
        // TODO: Implement tenant fetching from tenantService
        setTenants([{ id: 'all', name: 'All Tenants' }]);
      }
    } catch (err) {
      console.error('Error loading roles:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group roles by hierarchy level
  const roleGroups = [
    {
      level: 10,
      name: 'Super Administrator',
      icon: Shield,
      color: 'bg-red-500',
      borderColor: 'border-red-500',
      bgLight: 'bg-red-50',
      textColor: 'text-red-700',
      description: 'Platform-wide access to all features, tenants, and system configuration',
      capabilities: [
        'View and manage all tenants',
        'Switch to any Business Administrator view',
        'Access all system features and settings',
        'Manage platform-level configurations',
        'View complete audit trail across all tenants'
      ],
      roles: roles?.filter(r => r?.hierarchyLevel <= 10)
    },
    {
      level: 20,
      name: 'Business Administrator',
      icon: Building2,
      color: 'bg-purple-500',
      borderColor: 'border-purple-500',
      bgLight: 'bg-purple-50',
      textColor: 'text-purple-700',
      description: 'Full access within their organization/tenant',
      capabilities: [
        'Manage all users within tenant',
        'View all managers, staff, and customers',
        'Access all metrics and calendars',
        'Configure billing and subscriptions',
        'Manage integrations and settings'
      ],
      roles: roles?.filter(r => r?.hierarchyLevel > 10 && r?.hierarchyLevel <= 20)
    },
    {
      level: 40,
      name: 'Manager',
      icon: UserCog,
      color: 'bg-blue-500',
      borderColor: 'border-blue-500',
      bgLight: 'bg-blue-50',
      textColor: 'text-blue-700',
      description: 'Team management and oversight capabilities',
      capabilities: [
        'Manage assigned team members',
        'View team metrics and performance',
        'Access team calendars and schedules',
        'Approve staff actions and requests',
        'Generate team reports'
      ],
      roles: roles?.filter(r => r?.hierarchyLevel > 20 && r?.hierarchyLevel <= 40)
    },
    {
      level: 60,
      name: 'Staff Member',
      icon: Briefcase,
      color: 'bg-green-500',
      borderColor: 'border-green-500',
      bgLight: 'bg-green-50',
      textColor: 'text-green-700',
      description: 'Individual contributor with personal data access',
      capabilities: [
        'View own information and metrics',
        'Manage assigned customers',
        'Track personal sales and invoices',
        'Access personal calendar',
        'View own income and commissions'
      ],
      roles: roles?.filter(r => r?.hierarchyLevel > 40 && r?.hierarchyLevel <= 60)
    },
    {
      level: 100,
      name: 'Customer',
      icon: User,
      color: 'bg-gray-500',
      borderColor: 'border-gray-500',
      bgLight: 'bg-gray-50',
      textColor: 'text-gray-700',
      description: 'Customer portal access with personal account management',
      capabilities: [
        'View own account information',
        'Access scheduled services',
        'View and pay invoices',
        'Manage payment methods',
        'Update personal profile'
      ],
      roles: roles?.filter(r => r?.hierarchyLevel > 60)
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Super Admin Role Switching */}
      {isSuperAdmin() && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Eye className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-yellow-900">Super Admin View Switching</h3>
              <p className="text-sm text-yellow-700 mt-1">
                As a Super Administrator, you can switch to view any Business Administrator's scope
              </p>
              <div className="mt-3">
                <label className="block text-xs font-medium text-yellow-900 mb-1">
                  View as Tenant:
                </label>
                <select
                  value={selectedTenant || 'all'}
                  onChange={(e) => setSelectedTenant(e?.target?.value === 'all' ? null : e?.target?.value)}
                  className="w-full max-w-xs px-3 py-2 border border-yellow-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="all">All Tenants (Super Admin View)</option>
                  {tenants?.map(tenant => (
                    <option key={tenant?.id} value={tenant?.id}>
                      {tenant?.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Role Hierarchy Workflow */}
      <div className="space-y-4">
        {roleGroups?.map((group, index) => {
          const Icon = group?.icon;
          const hasRoles = group?.roles?.length > 0;

          return (
            <div key={group?.level} className="relative">
              {/* Connector Line */}
              {index < roleGroups?.length - 1 && (
                <div className="absolute left-6 top-full h-4 w-0.5 bg-gray-300 z-0"></div>
              )}

              {/* Role Group Card */}
              <div className={`border-2 ${group?.borderColor} rounded-lg overflow-hidden bg-white shadow-sm`}>
                {/* Header */}
                <div className={`${group?.color} text-white p-4`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold">{group?.name}</h3>
                        <p className="text-sm text-white/90">Hierarchy Level: {group?.level}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Users className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {group?.roles?.length} {group?.roles?.length === 1 ? 'Role' : 'Roles'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className={`${group?.bgLight} p-4`}>
                  <p className="text-sm text-gray-700 mb-4">{group?.description}</p>

                  {/* Capabilities */}
                  <div className="mb-4">
                    <h4 className={`text-xs font-semibold ${group?.textColor} uppercase tracking-wide mb-2`}>
                      Access & Capabilities
                    </h4>
                    <ul className="space-y-1.5">
                      {group?.capabilities?.map((capability, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                          <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                          <span>{capability}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Assigned Roles */}
                  {hasRoles && (
                    <div>
                      <h4 className={`text-xs font-semibold ${group?.textColor} uppercase tracking-wide mb-2`}>
                        Assigned Roles
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {group?.roles?.map(role => (
                          <div
                            key={role?.id}
                            className="bg-white border border-gray-200 rounded-md p-3 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {role?.displayName}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5 truncate">
                                  {role?.description}
                                </p>
                              </div>
                              <div className="ml-2 flex items-center space-x-2 flex-shrink-0">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                  <Users className="w-3 h-3 mr-1" />
                                  {role?.userCount}
                                </span>
                                {role?.isSystemRole && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                    System
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!hasRoles && (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500 italic">No roles assigned to this level</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Multi-Tenant Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-blue-900">White Label Multi-Tenant Architecture</h3>
            <p className="text-sm text-blue-700 mt-1">
              This role hierarchy is designed for multi-tenant white label platforms. Each tenant operates independently with their own Business Administrators, while Super Administrators maintain platform-wide oversight and can switch between tenant views for support and management purposes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}